// src/components/profile-input/steps/BasicInfoStep.jsx
import React, { useEffect, useState } from "react";
import { Grid, Typography, Divider, MenuItem, Select, InputAdornment, TextField } from "@mui/material";
import { TextInput, SelectInput } from "../FormFields";
import {
  options,
  generateProfileId
} from "../formUtils";
import { countryCodes, getDefaultCountryCode } from "../../../../components/data/countryCodes";

const BasicInfoStep = ({ formData, handleInputChange, errors = {} }) => {
  const [contactDisplay, setContactDisplay] = useState('');
  const [whatsappDisplay, setWhatsappDisplay] = useState('');
  const [contactCountryCode, setContactCountryCode] = useState(getDefaultCountryCode());
  const [whatsappCountryCode, setWhatsappCountryCode] = useState(getDefaultCountryCode());
  const [isContactCodeEditing, setIsContactCodeEditing] = useState(false);
  const [isWhatsappCodeEditing, setIsWhatsappCodeEditing] = useState(false);
  const [contactCodeInput, setContactCodeInput] = useState(getDefaultCountryCode());
  const [whatsappCodeInput, setWhatsappCodeInput] = useState(getDefaultCountryCode());

  // Auto-set date on component mount
  useEffect(() => {
    if (!formData.profile_created_date) {
      const currentDate = new Date();
      const day = String(currentDate.getDate()).padStart(2, '0');
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const year = currentDate.getFullYear();
      const formattedDate = `${day}-${month}-${year}`;
      handleInputChange("profile_created_date", formattedDate);
    }
  }, [formData.profile_created_date, handleInputChange]);

  // Initialize contact displays from form data
  useEffect(() => {
    if (formData.guardian_details?.country_code) {
      setContactCountryCode(formData.guardian_details.country_code);
      setContactCodeInput(formData.guardian_details.country_code);
    }
    
    if (formData.guardian_details?.contact_number) {
      setContactDisplay(formData.guardian_details.contact_number);
    }
    
    if (formData.guardian_details?.whatsapp_country_code) {
      setWhatsappCountryCode(formData.guardian_details.whatsapp_country_code);
      setWhatsappCodeInput(formData.guardian_details.whatsapp_country_code);
    }
    
    if (formData.guardian_details?.whatsapp_number) {
      setWhatsappDisplay(formData.guardian_details.whatsapp_number);
    }
  }, [
    formData.guardian_details?.contact_number,
    formData.guardian_details?.country_code,
    formData.guardian_details?.whatsapp_number,
    formData.guardian_details?.whatsapp_country_code
  ]);

  // Handle gender change
  const handleGenderChange = (e) => {
    const newGender = e.target.value;
    const newProfileId = generateProfileId(newGender);

    handleInputChange("personal_details.gender", newGender);
    handleInputChange("profile_id", newProfileId);
  };

  // Handle contact country code selection from dropdown
  const handleContactCountryCodeSelect = (e) => {
    const newCode = e.target.value;
    setContactCountryCode(newCode);
    setContactCodeInput(newCode);
    setIsContactCodeEditing(false);
    handleInputChange("guardian_details.country_code", newCode);
  };

  // Handle WhatsApp country code selection from dropdown
  const handleWhatsappCountryCodeSelect = (e) => {
    const newCode = e.target.value;
    setWhatsappCountryCode(newCode);
    setWhatsappCodeInput(newCode);
    setIsWhatsappCodeEditing(false);
    handleInputChange("guardian_details.whatsapp_country_code", newCode);
  };

  // Handle contact country code input change (when typing)
  const handleContactCodeInputChange = (e) => {
    const value = e.target.value;
    // Allow + and digits only
    const formattedValue = value.replace(/[^\d+]/g, '');
    setContactCodeInput(formattedValue);
  };

  // Handle WhatsApp country code input change (when typing)
  const handleWhatsappCodeInputChange = (e) => {
    const value = e.target.value;
    // Allow + and digits only
    const formattedValue = value.replace(/[^\d+]/g, '');
    setWhatsappCodeInput(formattedValue);
  };

  // Handle contact code input blur
  const handleContactCodeBlur = () => {
    setIsContactCodeEditing(false);
    // Check if the typed code exists in the list
    const matchedCountry = countryCodes.find(c => c.value === contactCodeInput);
    if (matchedCountry) {
      setContactCountryCode(matchedCountry.value);
    } else {
      // Keep the typed value if it doesn't match
      setContactCountryCode(contactCodeInput);
    }
    handleInputChange("guardian_details.country_code", contactCodeInput);
  };

  // Handle WhatsApp code input blur
  const handleWhatsappCodeBlur = () => {
    setIsWhatsappCodeEditing(false);
    // Check if the typed code exists in the list
    const matchedCountry = countryCodes.find(c => c.value === whatsappCodeInput);
    if (matchedCountry) {
      setWhatsappCountryCode(matchedCountry.value);
    } else {
      // Keep the typed value if it doesn't match
      setWhatsappCountryCode(whatsappCodeInput);
    }
    handleInputChange("guardian_details.whatsapp_country_code", whatsappCodeInput);
  };

  // Handle contact number input
  const handleContactNumberChange = (e) => {
    const value = e.target.value;
    // Allow only digits
    const digitsOnly = value.replace(/\D/g, '');
    setContactDisplay(digitsOnly);
    handleInputChange("guardian_details.contact_number", digitsOnly);
  };

  // Handle WhatsApp number input
  const handleWhatsappNumberChange = (e) => {
    const value = e.target.value;
    // Allow only digits
    const digitsOnly = value.replace(/\D/g, '');
    setWhatsappDisplay(digitsOnly);
    handleInputChange("guardian_details.whatsapp_number", digitsOnly);
  };

  // Force profile ID update if gender doesn't match
  useEffect(() => {
    if (formData.profile_id && formData.personal_details.gender) {
      const currentPrefix = formData.profile_id.charAt(0);
      const expectedPrefix = formData.personal_details.gender === "Male" ? "M" : "F";

      if (currentPrefix !== expectedPrefix) {
        const correctedProfileId = generateProfileId(formData.personal_details.gender);
        handleInputChange("profile_id", correctedProfileId);
      }
    }
  }, [formData.profile_id, formData.personal_details.gender, handleInputChange]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary">
          Profile Information
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      {/* Profile ID Field */}
      <Grid item xs={12} sm={4}>
        <TextInput
          label="Profile ID"
          value={formData.profile_id || ""}
          InputProps={{
            readOnly: true
          }}
          sx={{
            '& .MuiInputBase-input': {
              color: 'rgba(0, 0, 0, 0.6)',
              fontWeight: 'bold',
              fontSize: '14px'
            },
            '& .MuiInputBase-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
          helperText="Auto-generated"
        />
      </Grid>

      {/* Profile Created Date */}
      <Grid item xs={12} sm={4}>
        <TextInput
          label="Created Date"
          value={formData.profile_created_date || ""}
          InputProps={{ readOnly: true }}
          sx={{
            '& .MuiInputBase-input': {
              color: 'rgba(0, 0, 0, 0.6)'
            },
            '& .MuiInputBase-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        />
      </Grid>

      {/* Status */}
      <Grid item xs={12} sm={4}>
        <SelectInput
          label="Status"
          value={formData.status || "Active"}
          onChange={(e) => handleInputChange("status", e.target.value)}
          options={options.status}
          displayEmpty={false}
        />
      </Grid>

      {/* Gender */}
      <Grid item xs={12} sm={6}>
        <SelectInput
          label="Gender"
          value={formData.personal_details.gender || "Female"}
          onChange={handleGenderChange}
          options={options.gender}
          required={true}
          error={!!errors.gender}
          helperText={errors.gender}
          displayEmpty={false}
        />
      </Grid>

      {/* Profile Created By */}
      <Grid item xs={12} sm={6}>
        <SelectInput
          label="Created By"
          value={formData.profile_created_by || ""}
          onChange={(e) => handleInputChange("profile_created_by", e.target.value)}
          required={true}
          options={options.profile_creators}
          error={!!errors.profile_created_by}
          helperText={errors.profile_created_by}
          displayEmpty={false}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
          Guardian Details
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      {/* Guardian Name */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Guardian Name"
          value={formData.guardian_details.guardian_name || ""}
          onChange={(e) => handleInputChange("guardian_details.guardian_name", e.target.value)}
          required={true}
          error={!!errors.guardian_name}
          helperText={errors.guardian_name}
        />
      </Grid>

      {/* Relation */}
      <Grid item xs={12} sm={6}>
        <SelectInput
          label="Relation"
          value={formData.guardian_details.relation || "Father"}
          onChange={(e) => handleInputChange("guardian_details.relation", e.target.value)}
          options={options.relations}
          displayEmpty={false}
        />
      </Grid>

      {/* Guardian Contact with Editable Country Code */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Contact Number"
          value={contactDisplay}
          onChange={handleContactNumberChange}
          required={true}
          error={!!errors.guardian_contact}
          helperText={errors.guardian_contact || ""}
          placeholder="Enter phone number"
          inputProps={{
            type: "tel",
            inputMode: "numeric",
            maxLength: 15
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isContactCodeEditing ? (
                  <TextField
                    value={contactCodeInput}
                    onChange={handleContactCodeInputChange}
                    onBlur={handleContactCodeBlur}
                    size="small"
                    autoFocus
                    sx={{
                      width: '90px',
                      '& .MuiInputBase-input': {
                        padding: '4px 8px',
                        fontSize: '0.875rem'
                      },
                      '& fieldset': { border: 'none' }
                    }}
                    inputProps={{
                      style: { textAlign: 'center' }
                    }}
                  />
                ) : (
                  <Select
                    value={contactCountryCode}
                    onChange={handleContactCountryCodeSelect}
                    onOpen={() => setIsContactCodeEditing(false)}
                    size="small"
                    sx={{
                      '& .MuiSelect-select': {
                        padding: '4px 8px',
                        fontSize: '0.875rem',
                        minWidth: '70px',
                        cursor: 'pointer'
                      },
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }}
                  >
                    {countryCodes.map((country) => (
                      <MenuItem key={country.value} value={country.value}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </InputAdornment>
            ),
            onClick: (e) => {
              // Double-click to edit
              if (e.detail === 2) {
                setIsContactCodeEditing(true);
              }
            }
          }}
        />
      </Grid>

      {/* Guardian WhatsApp with Editable Country Code */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="WhatsApp Number"
          value={whatsappDisplay}
          onChange={handleWhatsappNumberChange}
          placeholder="Enter WhatsApp number"
          helperText=""
          inputProps={{
            type: "tel",
            inputMode: "numeric",
            maxLength: 15
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isWhatsappCodeEditing ? (
                  <TextField
                    value={whatsappCodeInput}
                    onChange={handleWhatsappCodeInputChange}
                    onBlur={handleWhatsappCodeBlur}
                    size="small"
                    autoFocus
                    sx={{
                      width: '90px',
                      '& .MuiInputBase-input': {
                        padding: '4px 8px',
                        fontSize: '0.875rem'
                      },
                      '& fieldset': { border: 'none' }
                    }}
                    inputProps={{
                      style: { textAlign: 'center' }
                    }}
                  />
                ) : (
                  <Select
                    value={whatsappCountryCode}
                    onChange={handleWhatsappCountryCodeSelect}
                    onOpen={() => setIsWhatsappCodeEditing(false)}
                    size="small"
                    sx={{
                      '& .MuiSelect-select': {
                        padding: '4px 8px',
                        fontSize: '0.875rem',
                        minWidth: '70px',
                        cursor: 'pointer'
                      },
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }}
                  >
                    {countryCodes.map((country) => (
                      <MenuItem key={country.value} value={country.value}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </InputAdornment>
            ),
            onClick: (e) => {
              // Double-click to edit
              if (e.detail === 2) {
                setIsWhatsappCodeEditing(true);
              }
            }
          }}
        />
      </Grid>
    </Grid>
  );
};

export default BasicInfoStep;